import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactTooltip from "react-tooltip";

import { Block, CaseAdmin, SpecimenProtocolOverride } from "pwapi";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { fullNameForBlock } from "shared/utils/blockUtils";
import {
  getLocalizedStringWithZone,
  getDateTimefromTimestamp,
} from "shared/utils/dates";
import {
  printBlocksLink,
  printSlidesLink,
  printSpecimensLink,
} from "shared/utils/printUtils";
import {
  getProtocolStatus,
  procedureEnumToLabel,
  protocolLabels,
  specimen_source,
} from "shared/utils/specimenUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";

import HiddenDropdown from "../filters/HiddenDropdown";
import SlidesTable from "../SlidesTable/SlidesTable";

type SpecimensProps = {
  adminCase: CaseAdminDetail;
  canUpdateCase: boolean;
};

const ErrorMessage = ({ message }: { message: string }) => {
  return (
    <span style={{ color: "var(--imperialRed)" }}>
      <FontAwesomeIcon icon="exclamation-circle" color="var(--imperialRed)" />{" "}
      {message}
    </span>
  );
};

const Specimens = ({ adminCase, canUpdateCase }: SpecimensProps) => {
  const navigate = useNavigate();
  const specimensHierarchy = useMemo(() => {
    // No specimens means no hierarchy
    if (!adminCase?.specimens?.length) return [];

    // Sorts the specimens and then continues to pull the specimen blocks
    return (
      adminCase.specimens
        .sort((a, b) =>
          a.name.localeCompare(b.name, "en", {
            numeric: true,
            sensitivity: "base",
          })
        )
        .map((spec) => {
          // The slides list which belongs solely to this specimen
          const specimenSlides = (adminCase.slides || []).filter(
            (slide) => slide.specimen_id === spec.id
          );
          // Filters the blocks to this specimen, sorts them, then maps them to push the slides pertaining to the individual blocks
          const filteredBlocks = (adminCase.blocks || [])
            .filter((blk) => blk.specimen_id === spec.id)
            .sort((a, b) =>
              (a.name || "").localeCompare(b.name || "", "en", {
                numeric: true,
                sensitivity: "base",
              })
            )
            .map((blk) => ({
              block: blk,
              slides: specimenSlides.filter(
                (slide) => slide.block_obj_id === blk.id
              ),
            }));

          // Helper variable to get all the block ids in a list for basSlides filter
          const specimenBlockIds = filteredBlocks.map(
            (blkDetails) => blkDetails.block.id
          );

          // Finds all the slides which belong to this specimen but either have no block or are matched to a block that isn't in the specimen
          const badSlides = specimenSlides.filter(
            (slide) =>
              !slide.block_obj_id ||
              !specimenBlockIds.includes(slide.block_obj_id)
          );

          // Returns an object which contains the specimen and its blocks, the blocks are an object of the block and its slides
          return {
            spec,
            blocks: [
              ...filteredBlocks,
              ...(badSlides?.length > 0
                ? [{ block: null, slides: badSlides }]
                : []),
            ],
          };
        }) || []
    );
  }, [adminCase.id, adminCase.specimens, adminCase.blocks, adminCase.slides]);

  return (
    <div className="panelContainer panelContainer--gap">
      <ReactTooltip />
      {specimensHierarchy.map((specimen, index) => {
        const specimenSource = specimen_source(specimen.spec, adminCase);
        const availableBlocks = specimen.blocks
          .map((b) => b.block)
          // TODO: TypeScript 5.5+ is smarter about this.
          .filter((v) => v != null) as Block[];

        return (
          <div
            className="SpecimensSection panelContainer__layout--vertical"
            key={`${specimen.spec.name}:${index}`}
          >
            <HiddenDropdown
              type="largeLabel"
              label={
                <span data-testid="specimen_header">
                  Specimen {specimen.spec.name}
                </span>
              }
              labelActions={
                <>
                  {canUpdateCase && (
                    <Link
                      to={`/admin/cases/${adminCase.id}/specimens/${specimen.spec.id}`}
                    >
                      <FontAwesomeIcon icon="pencil-alt" color="var(--steel)" />
                    </Link>
                  )}
                  {adminCase.source === CaseAdmin.source.PW && (
                    <>
                      {specimen.spec.protocols?.map((p) => (
                        <div
                          key={p}
                          style={{ marginBottom: "4px" }}
                          className="Tag Tag--blue"
                          data-tip={
                            getProtocolStatus(
                              specimen.spec,
                              p as SpecimenProtocolOverride.protocol
                            ).reason
                          }
                        >
                          {protocolLabels[p]}
                        </div>
                      ))}
                      <PathologyWatchButton
                        color="green"
                        className="SpecimensSection__addBlock"
                        size="small"
                        onClick={() => {
                          navigate(
                            `/admin/cases/${adminCase.id}/specimens/${specimen.spec.id}/blocks/new`
                          );
                        }}
                      >
                        + Add Block
                      </PathologyWatchButton>
                      {adminCase.id && (
                        <>
                          <Link
                            to={printSpecimensLink(adminCase.id, specimen.spec)}
                            target="_blank"
                            rel="opener"
                            className="PWTable__actions__link"
                          >
                            Print "{specimen.spec.name}" Label{" "}
                            <FontAwesomeIcon icon="print" />
                          </Link>
                          {!!availableBlocks?.length && (
                            <Link
                              to={printBlocksLink(
                                adminCase.id,
                                availableBlocks
                              )}
                              target="_blank"
                              rel="opener"
                              className="PWTable__actions__link"
                            >
                              Print All Blocks <FontAwesomeIcon icon="print" />
                            </Link>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              }
              isInitOpen
            >
              <div className="informationContainer">
                <label>Collected Date</label>
                <span>
                  {specimen.spec.collected_date ? (
                    getLocalizedStringWithZone(
                      getDateTimefromTimestamp(
                        specimen.spec.collected_date!,
                        adminCase?.collected_date_timezone
                      ),
                      DateTime.DATETIME_MED,
                      adminCase?.collected_date_timezone
                    )
                  ) : (
                    <ErrorMessage message="Missing Collected Date" />
                  )}
                </span>
                <label>Received Date</label>
                <span>
                  {specimen.spec.received_date ? (
                    getLocalizedStringWithZone(
                      getDateTimefromTimestamp(
                        specimen.spec.received_date!,
                        adminCase?.received_date_timezone
                      ),
                      DateTime.DATETIME_MED,
                      adminCase?.received_date_timezone
                    )
                  ) : (
                    <ErrorMessage message="Missing Received Date" />
                  )}
                </span>
                <label>Source:</label>
                <span>
                  {specimenSource ? (
                    specimenSource
                  ) : adminCase.source === CaseAdmin.source.WPL ? (
                    <ErrorMessage message="Missing Source Information" />
                  ) : (
                    <span>
                      {specimen.spec.type || (
                        <ErrorMessage message="Missing Type" />
                      )}
                      {", "}
                      {specimen.spec.site?.toUpperCase() || (
                        <ErrorMessage message="Missing Site" />
                      )}
                      {", "}
                      {specimen.spec.procedure ? (
                        procedureEnumToLabel(specimen.spec.procedure)
                      ) : (
                        <ErrorMessage message="Missing Procedure" />
                      )}
                    </span>
                  )}
                </span>
                <label>Clinical History:</label>
                <span>
                  {specimen.spec.clinical_history ? (
                    specimen.spec.clinical_history
                  ) : (
                    <ErrorMessage message="Missing Clinical History" />
                  )}
                </span>
                <label>Gross Description:</label>
                <span>
                  {specimen.spec.gross_description ? (
                    specimen.spec.gross_description
                  ) : (
                    <ErrorMessage message="Missing Gross Description" />
                  )}
                </span>
              </div>
            </HiddenDropdown>
            {specimen.blocks.length ? (
              specimen.blocks.map((blkDetails) => {
                return (
                  <span
                    style={{ marginLeft: "20px" }}
                    key={`${specimen.spec.id}${
                      blkDetails.block?.id ?? "issue"
                    }`}
                  >
                    <HiddenDropdown
                      label={
                        <span>
                          {blkDetails.block
                            ? `Block ${fullNameForBlock(
                                blkDetails.block,
                                specimen.spec
                              )}`
                            : "Slides with Block Issue"}
                        </span>
                      }
                      labelActions={
                        <>
                          {canUpdateCase && blkDetails.block && (
                            <>
                              <Link
                                to={`/admin/cases/${adminCase.id}/specimens/${specimen.spec.id}/blocks/${blkDetails.block.id}`}
                              >
                                <FontAwesomeIcon
                                  icon="pencil-alt"
                                  color="var(--steel)"
                                />
                              </Link>

                              <PathologyWatchButton
                                color="green"
                                size="small"
                                onClick={() => {
                                  navigate(
                                    `/admin/cases/${adminCase.id}/specimens/${specimen.spec.id}/blocks/${blkDetails.block.id}/slides/new`
                                  );
                                }}
                              >
                                + Add Slide
                              </PathologyWatchButton>
                              {adminCase.id && (
                                <>
                                  <Link
                                    to={printBlocksLink(
                                      adminCase.id,
                                      blkDetails.block
                                    )}
                                    target="_blank"
                                    rel="opener"
                                    className="PWTable__actions__link"
                                  >
                                    Print "
                                    {fullNameForBlock(
                                      blkDetails.block,
                                      specimen.spec
                                    )}
                                    " Label <FontAwesomeIcon icon="print" />
                                  </Link>
                                  {!!blkDetails.slides?.length && (
                                    <Link
                                      to={printSlidesLink(
                                        adminCase.id,
                                        blkDetails.slides
                                      )}
                                      target="_blank"
                                      rel="opener"
                                      className="PWTable__actions__link"
                                    >
                                      Print All Slides{" "}
                                      <FontAwesomeIcon icon="print" />
                                    </Link>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </>
                      }
                      isInitOpen
                      allowOverflow
                      hasError={!blkDetails.block}
                    >
                      {blkDetails.slides.length ? (
                        <SlidesTable
                          slides={blkDetails.slides}
                          slideEditUrl={`/admin/cases/${adminCase.id}/slides/`}
                        />
                      ) : (
                        <span>There are no slides in this block</span>
                      )}
                    </HiddenDropdown>
                  </span>
                );
              })
            ) : (
              <span>There are no blocks in this specimen</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Specimens;
